import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ImagePlus, X, Loader2, LocateFixed, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { useRefData } from "@/context/RefData";
import { catLabel } from "@/components/CategoryIcon";
import { ServicePicker, PromoteDialog, buyService, isProActive } from "@/components/Services";
import { AiDraft } from "@/components/AiAssistant";

const CONDS = ["new", "like_new", "good", "fair", "for_parts"];
const EMPTY = { title: "", description: "", category: "", price_eur: "", condition: "good", city: "", lat: null, lng: null,
  delivery_options: ["pickup"], quantity: 1, shipping_eur: 0, accepts_offers: true, accepts_exchange: false, images: [], attributes: {} };

const Photos = ({ images, setImages, t, limit }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const onFiles = async (files) => {
    setBusy(true);
    for (const f of Array.from(files).slice(0, limit - images.length)) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setImages((prev) => [...prev, data.url]);
      } catch (e) { toast.error(errMsg(e)); }
    }
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <div>
      <p className="label">{t("p2.sell.photos").replace("10", String(limit))}</p>
      <div className="grid grid-cols-3 gap-3 sm:grid-cols-5" data-testid="sell-photos">
        {images.map((u, i) => (
          <div key={u} className="relative aspect-square overflow-hidden rounded-2xl bg-[#F1F4F9]">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${u}`} alt="" className="h-full w-full object-cover" />
            {i === 0 && <span className="absolute left-1.5 top-1.5 rounded-full bg-[#3B1272] px-2 py-0.5 text-[10px] font-bold text-white">COVER</span>}
            <button type="button" onClick={() => setImages(images.filter((x) => x !== u))} data-testid={`sell-photo-remove-${i}`} className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1"><X className="h-3.5 w-3.5" /></button>
          </div>
        ))}
        {images.length < limit && (
          <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="sell-add-photo-btn" className="flex aspect-square flex-col items-center justify-center gap-1 rounded-2xl border-2 border-dashed border-[#C9B8EC] text-xs font-semibold text-[#3B1272] hover:bg-[#EFE9FB]">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}{busy ? t("p2.sell.uploading") : t("p2.sell.addPhoto")}
          </button>
        )}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => onFiles(e.target.files)} data-testid="sell-file-input" />
      <p className="mt-2 text-xs text-[#64748B]">{t("p2.sell.photoHint")}</p>
    </div>
  );
};

const Attributes = ({ attrs, setAttrs, t }) => {
  const [k, setK] = useState("");
  const [v, setV] = useState("");
  const add = () => { if (k.trim() && v.trim()) { setAttrs({ ...attrs, [k.trim()]: v.trim() }); setK(""); setV(""); } };
  return (
    <div>
      <p className="label">{t("p2.sell.attrs")}</p>
      <div className="flex flex-wrap gap-2">{Object.entries(attrs).map(([a, b]) => (
        <span key={a} className="pill bg-[#EFE9FB] text-[#3B1272]">{a}: {b}<button type="button" onClick={() => { const n = { ...attrs }; delete n[a]; setAttrs(n); }}><X className="h-3 w-3" /></button></span>))}</div>
      <div className="mt-2 flex gap-2">
        <input className="field" placeholder={t("p2.sell.attrKey")} value={k} onChange={(e) => setK(e.target.value)} data-testid="sell-attr-key" />
        <input className="field" placeholder={t("p2.sell.attrVal")} value={v} onChange={(e) => setV(e.target.value)} data-testid="sell-attr-value" />
        <button type="button" onClick={add} className="btn-ghost shrink-0" data-testid="sell-attr-add"><Plus className="h-4 w-4" />{t("p2.sell.addAttr")}</button>
      </div>
    </div>
  );
};

export default function SellPage() {
  const { t, lang } = useI18n();
  const { user } = useAuth();
  const { categories, cities } = useRefData();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("edit");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [service, setService] = useState("");
  const [promote, setPromote] = useState(null);
  const { data: existing } = useQuery({ queryKey: ["my-listing", editId], enabled: !!editId,
    queryFn: async () => (await api.get("/my/listings")).data.find((l) => l.listing_id === editId) });
  useEffect(() => {
    if (existing) setF({ ...EMPTY, ...existing, price_eur: existing.price_cents / 100, shipping_eur: (existing.shipping_cents || 0) / 100, city: existing.location.city, lat: null, lng: null });
  }, [existing]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const toggleDelivery = (d) => setF({ ...f, delivery_options: f.delivery_options.includes(d) ? f.delivery_options.filter((x) => x !== d) : [...f.delivery_options, d] });
  const locate = () => navigator.geolocation?.getCurrentPosition(
    (p) => { setF((s) => ({ ...s, lat: p.coords.latitude, lng: p.coords.longitude })); toast.success(t("p2.sell.posOk")); },
    () => toast.error(t("search.geoDenied")));
  const submit = async (publish) => {
    if (!f.images.length) return toast.error(t("p2.sell.needPhoto"));
    setBusy(true);
    const body = { title: f.title, description: f.description, category: f.category, attributes: f.attributes, images: f.images,
      price_eur: Number(f.price_eur), condition: f.condition, city: f.city, lat: f.lat, lng: f.lng, delivery_options: f.delivery_options,
      quantity: Number(f.quantity) || 1, shipping_eur: Number(f.shipping_eur) || 0, accepts_offers: f.accepts_offers, accepts_exchange: f.accepts_exchange };
    try {
      if (editId) { await api.patch(`/my/listings/${editId}`, body); toast.success(t("p2.sell.saved")); }
      else {
        const { data } = await api.post("/my/listings", { ...body, publish });
        toast.success(t("p2.sell.created"));
        if (service && publish && data.status === "published") return await buyService(service, data.listing_id);
      }
      nav("/account?tab=listings");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const verified = user?.email_verified;
  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-8" data-testid="sell-page">
      <h1 className="text-3xl font-bold sm:text-4xl">{editId ? t("p2.sell.editTitle") : t("p2.sell.title")}</h1>
      <p className="mt-2 text-sm text-[#4B4763]">{t("p2.sell.free")}</p>
      {!verified && !editId && <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="sell-verify-warning">{t("p2.sell.verifyFirst")}</p>}
      <form className="card-soft mt-6 space-y-6 p-6" onSubmit={(e) => { e.preventDefault(); submit(true); }}>
        <Photos images={f.images} limit={isProActive(user) ? 22 : 10} setImages={(fn) => setF((s) => ({ ...s, images: typeof fn === "function" ? fn(s.images) : fn }))} t={t} />
        <AiDraft onApply={(d) => setF((s) => ({ ...s, title: d.title || s.title, description: d.description || s.description, category: d.category || s.category }))} />
        <div><label className="label">{t("p2.sell.titleF")}</label><input className="field" required minLength={5} maxLength={90} value={f.title} onChange={set("title")} data-testid="sell-title-input" /></div>
        <div><label className="label">{t("p2.sell.desc")}</label><textarea className="field min-h-[130px]" required minLength={20} maxLength={4000} value={f.description} onChange={set("description")} data-testid="sell-description-input" /></div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div><label className="label">{t("p2.sell.category")}</label>
            <select className="field" required value={f.category} onChange={set("category")} data-testid="sell-category-select"><option value="">—</option>{categories.map((c) => <option key={c.id} value={c.id}>{catLabel(c, lang)}</option>)}</select></div>
          <div><label className="label">{t("p2.sell.condition")}</label>
            <select className="field" value={f.condition} onChange={set("condition")} data-testid="sell-condition-select">{CONDS.map((c) => <option key={c} value={c}>{t(`cond.${c}`)}</option>)}</select></div>
          <div><label className="label">{t("p2.sell.price")}</label><input className="field" type="number" min="0" step="0.01" required value={f.price_eur} onChange={set("price_eur")} data-testid="sell-price-input" /></div>
          {user?.role === "pro" && <div><label className="label">{t("p2.sell.quantity")}</label><input className="field" type="number" min="1" max="999" value={f.quantity} onChange={set("quantity")} data-testid="sell-quantity-input" /></div>}
          <div><label className="label">{t("p2.sell.city")}</label>
            <select className="field" required value={f.city} onChange={set("city")} data-testid="sell-city-select"><option value="">—</option>{cities.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}</select>
            <button type="button" onClick={locate} className="mt-2 flex items-center gap-1 text-xs font-semibold text-[#3B1272]" data-testid="sell-locate-btn"><LocateFixed className="h-3.5 w-3.5" />{f.lat ? t("p2.sell.posOk") : t("p2.sell.useMyPos")}</button></div>
        </div>
        <div><p className="label">{t("p2.sell.delivery")}</p><div className="flex gap-4">{["pickup", "shipping"].map((d) => (
          <label key={d} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.delivery_options.includes(d)} onChange={() => toggleDelivery(d)} data-testid={`sell-delivery-${d}`} />{t(`delivery.${d}`)}</label>))}</div>
          {f.delivery_options.includes("shipping") && <div className="mt-3 max-w-xs"><label className="label">{t("p3.shipping")} (€)</label><input className="field" type="number" min="0" step="0.01" value={f.shipping_eur} onChange={set("shipping_eur")} data-testid="sell-shipping-input" /></div>}</div>
        <div className="flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.accepts_offers} onChange={set("accepts_offers")} data-testid="sell-offers-checkbox" />{t("p2.sell.offers")}</label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.accepts_exchange} onChange={set("accepts_exchange")} data-testid="sell-exchange-checkbox" />{t("p2.sell.exchange")}</label>
        </div>
        <Attributes attrs={f.attributes} setAttrs={(a) => setF({ ...f, attributes: a })} t={t} />
        <div className="border-t border-[#EEF0F6] pt-5" data-testid="sell-services">
          <p className="label">{t("p4.optional")}</p>
          {editId ? (existing?.status === "published" && <button type="button" onClick={() => setPromote(editId)} className="btn-ghost" data-testid="sell-promote-btn">{t("p4.promote")}</button>)
            : <><ServicePicker value={service} onChange={setService} withNone /><p className="mt-2 text-xs text-[#64748B]">{t("p4.afterPublish")} {t("p4.promoteNote")}</p></>}
        </div>
        <div className="flex flex-wrap justify-end gap-3 border-t border-[#EEF0F6] pt-5">
          <Link to="/account?tab=listings" className="btn-ghost">{t("common.close")}</Link>
          {!editId && <button type="button" disabled={busy} onClick={() => submit(false)} className="btn-ghost" data-testid="sell-save-draft-btn">{t("p2.sell.saveDraft")}</button>}
          <button type="submit" disabled={busy || (!editId && !verified)} className="btn-coral" data-testid="sell-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{editId ? t("p2.sell.save") : service ? t("p4.publishPay") : t("p2.sell.publish")}</button>
        </div>
      </form>
      <PromoteDialog listingId={promote} onClose={() => setPromote(null)} />
    </div>
  );
}
